import React, { useEffect, useState } from "react";
import { Button } from "../../../../../../shadcn/components/ui/button";
import {
  getBidderByUuid,
  initiateBidApplicationDownloadAndSave,
  initiateConsentNotificationForPrimary,
  updateBidderForEvent,
} from "services/bidder";
import { useSelector } from "react-redux";
import CustomLoadingModal from "../IndividualBidders/CustomLoadingModal";
interface BidderBidApplicationDownloadComponentProps {
  bidderUuid: any;
  bidder: any;
  stepsTitles: any;
  activeStep: any;
  setActiveStep: any;
  init: any;
  outerNextStep?: any;
  requiresApproverDetails?: boolean;
}
export default function JBBidderBidApplicationDownloadComponent({
  bidderUuid,
  bidder,
  stepsTitles,
  activeStep,
  setActiveStep,
  init,
  outerNextStep,
  requiresApproverDetails = true,
}: BidderBidApplicationDownloadComponentProps) {
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const [isGranted, setIsGranted] = useState<any>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingText, setLoadingText] = useState<string>(
    "Generating bid application. Please wait...",
  );
  const imgixBaseUrl = process.env.NEXT_PUBLIC_IMAGIX_URL;

  useEffect(() => {
    setIsGranted(bidder?.esigned_data?.is_kyc_file_persisted === true);
  }, [bidder, bidder?.esigned_data?.file_uuid]);

  const handleGenerate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res: any = await initiateBidApplicationDownloadAndSave({
        bidder_onboarding_uuid: onboardingData.uuid,
        eligible_bidders_uuid: bidderUuid,
      });
      await init(bidderUuid);
    } catch (error: any) {
      console.log("error: ", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate bid application. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoadingText("Submitting data. Please wait...");
    setIsLoading(true);
    const data: any = await getBidderByUuid(bidderUuid);
    const requiresApproverDetails = outerNextStep !== "completed";
    const isSkipApproverPath =
      outerNextStep === "completed" || !requiresApproverDetails;
    const bidderRelations = data?.bidder_relations || [];
    const consentData = [];
    for (const bidderData of bidderRelations) {
      consentData.push({
        bidder_uuid: bidderData?.bidder_uuid,
        primary_bidder_uuid: bidderData?.primary_bidder_uuid,
        consent_status: bidderData.type === "primary" ? "approved" : "pending",
      });
    }
    console.log("consentData: ", consentData);

    if (data?.esigned_data?.consent_notified !== true) {
      // sent consent emails to joint bidders
      try {
        await initiateConsentNotificationForPrimary({
          eligible_bidders_uuid: bidderUuid,
        });
      } catch (error) {
        console.log("error: ", error);
      }
    }
    await updateBidderForEvent({
      uuid: bidderUuid,
      meta: {
        ...data?.meta,
        ...(isSkipApproverPath
          ? { submission_time: new Date().toISOString() }
          : {}),
        steps_status: {
          ...data?.meta?.steps_status,
          bid_application_download: "completed",
          ...(isSkipApproverPath ? { approver_details: "completed" } : {}),
        },
      },
      verification_step: isSkipApproverPath
        ? "wait_for_consent"
        : "approver_details",
      status: "pending",
      esigned_data: {
        ...data?.esigned_data,
        consent_data: consentData,
        consent_notified: true,
      },
    });
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setActiveStep(activeStep + 1);
    await init();
    setIsLoading(false);
    setLoadingText("Generating bid application. Please wait...");
  };
  return (
    <div className="py-4 font-openSans">
      <CustomLoadingModal isOpen={isLoading} loadingText={loadingText} />
      <div className="p-4 space-y-4 bg-white">
        {isGranted && (
          <div className="space-y-4">
            <p className="text-gray-600 text-xs md:text-sm">
              Your bid application has been generated. Please download it below.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={
                  bidder?.esigned_data?.file_uuid
                    ? `${imgixBaseUrl}/${bidder.esigned_data.file_uuid}`
                    : "#"
                }
                download={`bid_application_${
                  bidder?.meta?.event_id || "bidder"
                }.pdf`}
                className="flex-1"
              >
                <Button
                  className="w-full h-8 text-xs font-medium"
                  variant="default"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5 mr-1.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download Application
                </Button>
              </a>
              {bidder?.esigned_data?.file_uuid && (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${imgixBaseUrl}/${bidder.esigned_data.file_uuid}`}
                  className="flex-1"
                >
                  <Button
                    className="w-full h-8 text-xs font-medium"
                    variant="outline"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5 mr-1.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                    View Document
                  </Button>
                </a>
              )}
            </div>
          </div>
        )}

        {!isGranted && (
          <div className="space-y-2">
            <p className="text-gray-600 text-xs md:text-sm">
              Please generate your bid application and follow the instructions
              below.
            </p>
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-xs md:text-sm font-openSans">
                  {error}
                </p>
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-sm border p-4 space-y-4">
          <h4 className="text-sm md:text-sm font-semibold text-gray-700">
            {`What's Next`}
          </h4>
          <ol className="space-y-4 text-gray-700 text-xs md:text-sm leading-relaxed">
            {!isGranted && (
              <li className="flex items-start gap-3">
                {/* Bullet Icon */}
                <span className="bg-brand-orange/50 text-brand-orange rounded-full w-3 h-3 mt-1 flex-shrink-0"></span>

                <p>
                  Click{" "}
                  <span className="font-semibold">
                    “Generate Bid Application”
                  </span>
                  . The system will automatically prepare the document based on
                  the details you submitted in previous steps.
                </p>
              </li>
            )}

            <li className="flex items-start gap-3">
              <span className="bg-brand-headingBlue/50 text-brand-headingBlue rounded-full w-3 h-3 mt-1 flex-shrink-0"></span>

              <p>
                Download the generated bid application and submit it to your
                bank for processing.
              </p>
            </li>

            {requiresApproverDetails ? (
              <li className="flex items-start gap-3">
                <span className="bg-brand-bgSuccessGreen/50 text-brand-bgSuccessGreen rounded-full w-3 h-3 mt-1 flex-shrink-0"></span>
                <p>
                  After submitting the document, collect the bank
                  receipt/acknowledgement. These receiver details will be
                  required for the next step (KYC validation).
                </p>
              </li>
            ) : (
              <li className="flex items-start gap-3">
                <span className="bg-brand-bgSuccessGreen/50 text-brand-bgSuccessGreen rounded-full w-3 h-3 mt-1 flex-shrink-0"></span>
                <p>
                  Please review your details and proceed with Final Submission
                  to complete your bid application. A confirmation email will be
                  sent after verification.
                </p>
              </li>
            )}
          </ol>
        </div>

        {!isGranted && (
          <div className="bg-white border rounded-xl p-4">
            <h4 className="text-sm md:text-lf font-semibold text-brand-textBlack mb-2">
              Generate Your Bid Application
            </h4>
            <p className="text-gray-600 text-xs md:text-sm">
              Click the button below to generate your official bid application
              document.
            </p>
            <div className="mt-6 mb-10 md:mb-0 flex">
              <Button
                className="w-full max-w-xl h-9 text-[14px]"
                onClick={handleGenerate}
                disabled={isLoading}
              >
                {isLoading ? "Generating..." : "Generate Bid Application"}
              </Button>
            </div>
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-xs md:text-sm font-openSans">
                  {error}
                </p>
              </div>
            )}
          </div>
        )}

        {isGranted && (
          <>
            <div className="mt-6 flex">
              <Button
                className="w-full max-w-xl h-10 text-[18px]"
                onClick={handleSubmit}
              >
                {requiresApproverDetails ? "Next" : "Final Submission"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
